import { LitElement, html, css, TemplateResult, PropertyValues } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { TerminalsModel } from '../../../src/models/terminals-model';
import { UiStateModel } from '../../../src/models/ui-state-model';
import { deepCopy } from '../../../src/utils/helpers';
import { VesselPlanningModel } from '../../../src/models/vessel-planning-model';
import './voy-vessel-gsis-list-entry';
import { VoyVesselGsisListEntry } from './voy-vessel-gsis-list-entry';
import { ScheduleEntry, SimplifiedPortCall } from '../../../src/models/types/voyage-schedules-model.types';
import { VoyageSchedulesModel } from '../../../src/models/voyage-schedules-model';
import { SuggestedRouteEvents } from '../../voyage-suggested-routes/src/types';
import { VoyageImoModel } from '../../../src/models/voyage-imo-model';
import { VesselNotificationsModel } from '../../../src/models/vessel-notifications-model';
import '@maersk-global/mds-components-core/mc-loading-indicator';

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;

@customElement('voyage-vessel-details')
export class VoyageVesselDetails extends LitElement
{
    @property({type: Number}) imo: number = 0;

    constructor() {
        super();

        this._refreshSchedule = this._refreshSchedule.bind(this);
        this._handleNetworkChange = this._handleNetworkChange.bind(this);
        this._onWrStatusToggled = this._onWrStatusToggled.bind(this);
    }

    static get styles() {
        return [
            css`
            :host {
                display: flex;
                flex-direction: column;
                max-height: calc(100vh - 180px);
            }

            .name {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                font-family: var(--component-heading-ff, 'serif');
                font-size: 30px;
                line-height: 41px;
                font-weight: var(--component-heading-weight, normal);

                color: var(--neutrals-grey-02, black);
                text-align: center;
            }

            .name-sm {
                font-size: 25px;
            }

            .name-xs {
                font-size: 20px;
            }

            .vesselhelp {
                margin-left: 43px;
                margin-top: 8px;
                margin-bottom: 20px;
                font-family: MaerskText;
                font-size: 12px;
                line-height: 16px;
                color: var(--neutrals-grey-03, black);
            }

            .scrollContainer {
                overflow-y: scroll;
                flex-shrink: 1;
            }

            .gsislist {
                position: relative;
                display: flex;
                flex-direction: row;
                min-height: 105px;
                justify-content: center;

                font-family: var(--component-paragraph-ff, 'serif');
                font-size: var(--component-paragraph-size, 20px);
                line-height: var(--component-paragraph-height, 20px);
                font-weight: var(--component-paragraph-weight, normal);
            }

            button {
                width: 32px;
                margin: 5px;
                background: none;
                border: none;
                outline: none;
                cursor: pointer;
            }

            button > svg {
                width: 10px;
                fill: var(--neutrals-grey-02, black);
            }

            /* The actual timeline (the vertical ruler) */
            .timeline {
                position: relative;
                width: 12px;
                background-color: var(--neutrals-grey-08, grey);
                margin-left: 35px;
                margin-top: 10px;
                margin-bottom: 10px;
                border-radius: 5px;
            }

            .timeline::after {
                content: '';
                position: absolute;
                top: 0px;
                left: 0px;
                background-color: var(--primary-blue-04, blue);

                width: 100%;
                /* Subtract timeline's margin-top (10px) since ::after starts inside .timeline, not from .gsislist top */
                height: calc(var(--vessel-timeline-progress-height, 0px) - 10px);
                border-radius: 5px;
            }

            .entries {
                height: fit-content;
            }

            h2 {
                position: relative;
                color: var(--primary-blue-04, blue);
                font-size: 14px;
                margin-bottom: 0px;
            }

            /* Draw circle to the left (on top of the timeline) */
            h2::before {
                content: '';
                position: absolute;
                width: 8px;
                height: 8px;
                left: -31px;
                background-color: var(--neutrals-grey-10, white);
                top: 6px;
                border-radius: 50%;
            }

            /* The actual content */
            .content {
                background-color: var(--neutrals-grey-10, white);
                position: relative;
                font-size: 12px;
                border: 1px solid var(--neutrals-grey-06, grey);
                margin: 10px;
                padding: 0px 0px 10px 10px;
                min-width: 250px;
            }

            .content[first] {
                margin-top: 7px;
            }

            .content h2 {
                text-transform: uppercase;
            }

            .content div {
                color: grey;
                text-transform: uppercase;
            }

            .in-port, .vessel-progress {
                position: absolute;
                z-index:1;
            }

            .in-port {
                /* Center the 16px-tall anchor icon at the progress tip */
                top: calc(var(--vessel-timeline-progress-height, 0px) - 8px);
                left: 29px;
            }

            .in-port > svg {
                width: 25px;
                height: 30px;
            }

            .vessel-progress {
                transform: rotate(90deg);
                /* Center the rotated vessel icon at the progress tip */
                top: calc(var(--vessel-timeline-progress-height, 0px) - 15px);
                left: 23px;
            }
            .vessel-progress > svg {
                width: 32px;
                height: 30px;
            }

            .noSchedule {
                margin-left: 40px;
                margin-right: 40px;
                margin-top: 8px;
                margin-bottom: 20px;
                font-family: MaerskText;
                text-align: center;
                display: flex;
                justify-content: center;
                align-items: center;
                font-size: 16px;
                color: var(--neutrals-grey-04, black);
            }

            .anchor {
                color: var(--neutrals-grey-04, grey);
                margin-left: 10px;
                font-size: 12px;
            }

            .anchor > svg {
                width: 14px;
                fill: currentColor;
                padding-right: 30px;
                vertical-align: text-bottom;
            }

            .editScheduleContainer {
                width: 100%;
                border-top: 1px solid var(--neutrals-grey-07, grey);
            }

            .editScheduleBtn {
                padding: 0px;
                width: calc(100% - 20px);
                height: 32px;
                font-family: MaerskText;
                font-size: 14px;
                margin: 10px;
                background-color: var(--primary-blue-05, blue);
                color: var(--neutrals-grey-10, white);
                border-radius: 2.5px;
            }
            .editScheduleBtn.disable {
                opacity: 0.5;
                cursor: not-allowed;
            }
        `];
    }

    private isDisabled = false;

    render() {
        let activeEntry = null;
        if (this._schedule && this._schedule.length > 0) {
            let activeIdx = this._schedule.findIndex(e => e.active);
            if (activeIdx === -1) {
                activeIdx = 0;
            }
            activeEntry = this._schedule[activeIdx];
            let timelineHeight = activeIdx * 192;
            timelineHeight += activeEntry.progress > 0 ? Math.ceil(((activeEntry.progress + 45) / 100) * 60) : 0;
            this.style.setProperty("--vessel-timeline-progress-height", `${timelineHeight}px`);
        }

        const editSchedulesClass = (this.isDisabled) ? "editScheduleBtn  disable" : "editScheduleBtn";

        return html`
            <div class="vesselhelp">
                Mouse hover a voyage to make changes
            </div>
            <div class="scrollContainer">
                <div class="gsislist">
                    ${ this._isLoadingSchedule ? html`<div class="noSchedule"><mc-loading-indicator fit="small"></mc-loading-indicator></div>` 
                    : (!this._schedule || this._schedule.length === 0) ? html`<div class="noSchedule">Schedule unavailable</div>` : html`
                    ${activeEntry!.inPort ? html`<div class='in-port'>${SvgIcons.anchor}</div>` : html`<div class='vessel-progress'>${SvgIcons.customVessel}</div>`}
                    <div class="timeline" title="${activeEntry && activeEntry.progress > 1 ? activeEntry.progress + "% of voyage completed" : ""}"></div>
                    <div class="entries">
                        ${ this._schedule.map((item, index) => this.renderEntry(item, index))}
                    </div>
                    `}
                </div>
            </div>
            
            <div class="editScheduleContainer">
                <button data-test="edit-schedule-btn" ?disabled=${this.isDisabled} class=${editSchedulesClass} @click=${this._editScheduleClicked}>Edit Schedule...</button>
            </div>
        `
    }

    renderEntry(scheduleEntry: ScheduleEntry, index: number): TemplateResult
    {
        return html`
            <voy-vessel-gsis-list-entry .scheduleEntry=${scheduleEntry}
                                        .omittedPortCall=${this._portOmitsMap.get(scheduleEntry.id) || null}                 
            ></voy-vessel-gsis-list-entry>
            ${ (index < this._schedule.length-1) ? html `
            <div data-test="anchorDuration" class="anchor">${SvgIcons.anchor} ${this._formatAnchoringDate(scheduleEntry.eta, this._schedule[index+1].etd)}</div>
            ` : html``
            }
        `;
    }

    goBack() {
        UiStateModel!.setValue('imo-details');
        this.imo = 0;
    }

    updated(changedProperties: PropertyValues) {
        if (changedProperties.has('imo')) {
            if (this.imo !== 0) {
                this._refreshSchedule();
            } else {
                this._schedule = [];
            }
        }
        this._calculateVesselProgressHeight();
    }

    private _handleNetworkChange(evt: Event) {
        // Update state based on event detail
        const e = evt as CustomEvent;
        this.isDisabled = !e.detail.online;
        this.requestUpdate();
    }

    connectedCallback()
    {
        super.connectedCallback();

        VesselPlanningModel!.addEventListener(SuggestedRouteEvents.PlanChanged, this._refreshSchedule);
        VoyageSchedulesModel!.addEventListener('schedule-updated', this._refreshSchedule);
        window.addEventListener('network-change', this._handleNetworkChange);
        this.addEventListener('wr-status-toggled', this._onWrStatusToggled);

        this._refreshSchedule();
    }


    disconnectedCallback()
    {
        super.disconnectedCallback();

        VesselPlanningModel!.removeEventListener(SuggestedRouteEvents.PlanChanged, this._refreshSchedule);
        VoyageSchedulesModel!.removeEventListener('schedule-updated', this._refreshSchedule);
        window.removeEventListener('network-change', this._handleNetworkChange);
        this.removeEventListener('wr-status-toggled', this._onWrStatusToggled);

        if (this._refreshInterval) {
            clearInterval(this._refreshInterval);
            this._refreshInterval = 0;
        }
    }

    @property({attribute: false}) _schedule: ScheduleEntry[] = [];
    @property({attribute: false}) _isLoadingSchedule: boolean = true;
    private _refreshInterval: number = 0;
    private _portOmitsMap: Map<string,SimplifiedPortCall> = new Map();
    
    private async _calculateVesselProgressHeight()
    {
        // Calculate progress height when using the variable height voy-vessel-gsis-list-entry elements
        const entries: NodeListOf<VoyVesselGsisListEntry> = this.shadowRoot!.querySelectorAll("voy-vessel-gsis-list-entry");
        for (const entry of entries)
        {
            if (!entry.scheduleEntry)
            {
                // Elements not ready yet
                break;
            }

            if (entry.scheduleEntry.active)
            {
                const portPositions = await entry.getPortsPositionInfo();
                const entryRect = entry.getBoundingClientRect();
                const gsislistRect = this.shadowRoot!.querySelector(".gsislist")!.getBoundingClientRect();
                let timelineHeight = entryRect.top - gsislistRect.top - 7 + portPositions.departure.bottom;
                if (entry.scheduleEntry.progress > 0)
                {
                    timelineHeight += (entry.scheduleEntry.progress/100) * (portPositions.arrival.top - portPositions.departure.bottom - 3);
                }
                this.style.setProperty("--vessel-timeline-progress-height", `${timelineHeight}px`);
                break;
            }
        }
    }

    private async _refreshSchedule() {
        if (this._schedule.length === 0) {
            this._isLoadingSchedule = true;
        }
        let schedule: ScheduleEntry[] = [];
        try {
            schedule = deepCopy(await VoyageSchedulesModel!.getScheduleFor(this.imo) || []);
        } catch (err) {
            console.warn(`Failed to load schedule for vessel ${this.imo}`, err);
            this._isLoadingSchedule = false;
            return;
        }
        this._isLoadingSchedule = false;
        if(isVesselOnCloud && schedule.length > 0) {
            VoyageImoModel?.dispatchEvent(new Event('hide-loader'));
        };
        this._portOmitsMap.clear();

        // Cut off schedule after 12 weeks from now
        const cutoffDate = Date.now() + 12*7*24*60*60*1000;
        const getArrivalTimestamp = (entry: ScheduleEntry) => {
            const arrivalAt = entry.arrivalPortCall?.GSIS?.arrivalAt || entry.arrivalAt;
            const timestamp = new Date(arrivalAt).getTime();
            return Number.isFinite(timestamp) ? timestamp : 0;
        };
        const filteredSchedule = schedule.filter(entry => getArrivalTimestamp(entry) <= cutoffDate);

        // Omitted legs will be shown as an omitted port call instead of an entry in the schedules list
        for (let i = 0; i < filteredSchedule.length; i++)
        {
            const entry = filteredSchedule[i];
            if (entry.omitted)
            {
                if (i > 0)
                {
                    this._portOmitsMap.set(filteredSchedule[i-1].id, entry.arrivalPortCall!.current);
                }
                filteredSchedule.splice(i, 1);
                i--;
            }
        }        

        this._schedule = filteredSchedule;
        if(filteredSchedule.length > 0) {
            VesselNotificationsModel!.dispatchEvent(new CustomEvent('update-activity-log', {
                detail:{
                    schedules: filteredSchedule,
                }
            }));
        }
    }

    private _extractPort(port: string) {
        return TerminalsModel!.getCity(port) || port;
    }

    private _formatAnchoringDate(date1: number, date2: number) {
        let difference = (date2 - date1) / 60; 
        if (difference < 0){
            return '';
        }

        let hours = Math.floor(difference / 60 / 1000);
        let days = Math.floor(hours / 24);
        hours = hours - (days * 24);
        return `${days} ${((days == 1) ? ' Day' : ' Days')} ${hours} Hours ${Math.floor((difference % 60000) / 1000)} Minutes`;
    }

    private _editScheduleClicked()
    {
        this.dispatchEvent(new Event("edit-schedule"));
    }

    private _onWrStatusToggled()
    {
        // Recalculate progress height when WR status is expanded/collapsed
        this._calculateVesselProgressHeight();
    }
}
